import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { RiFacebookCircleFill, RiWhatsappFill, RiArrowLeftLine, RiExternalLinkLine, RiPhoneFill, RiMailFill } from 'react-icons/ri';
import { parseVehicleId, vehicleUrlParam } from '../utils/vehicleUrl';

function ProviderBadge({ name, bgColor, textColor }) {
  if (!name) return null;
  return (
    <span style={{
      background: bgColor || '#555555', color: textColor || '#ffffff',
      fontSize: 11, fontWeight: 700,
      padding: '4px 10px', borderRadius: 20,
      letterSpacing: '.3px', whiteSpace: 'nowrap',
    }}>
      {name}
    </span>
  );
}

function cleanPhone(phone) {
  if (!phone) return null;
  return phone.replace(/\D/g, '');
}

function whatsappUrl(phone, message) {
  const clean = cleanPhone(phone);
  if (!clean) return null;
  const num = clean.startsWith('56') ? clean : `56${clean.startsWith('9') ? clean : '9' + clean}`;
  return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
}

export default function VehicleDetailPage() {
  const { state: stateVehicle } = useLocation();
  const { slug } = useParams();
  const id = parseVehicleId(slug);
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(stateVehicle || null);
  const [loading, setLoading] = useState(!stateVehicle);
  const [imgIndex, setImgIndex] = useState(0);
  const [contact, setContact] = useState(null);

  useEffect(() => {
    if (!stateVehicle && id) {
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/search/vehicles/cache/${id}`)
        .then(r => r.ok ? r.json() : Promise.reject(r.status))
        .then(data => { setVehicle(data); setLoading(false); })
        .catch(() => { setVehicle(null); setLoading(false); });
    }
  }, [id, stateVehicle]);

  // Para publicaciones propias, busca datos de contacto
  useEffect(() => {
    if (!vehicle) return;
    const isOwn = vehicle.url?.startsWith('/publicar/');
    if (!isOwn) return;
    const listingId = vehicle.url.replace('/publicar/', '');
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/listings/${listingId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setContact({ name: data.contactName, phone: data.contactPhone, email: data.contactEmail }); })
      .catch(() => {});
  }, [vehicle]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px 20px' }}><p>Cargando...</p></div>;
  }

  if (!vehicle) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p>Vehículo no encontrado.</p>
        <button onClick={() => navigate('/')} style={{ color: '#C31617', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
          ← Volver al inicio
        </button>
      </div>
    );
  }

  const images = vehicle.images || [];
  const img = images[imgIndex];
  const isOwn = vehicle.url?.startsWith('/publicar/');
  const shareUrl = `${window.location.origin}/vehiculo/${slug}`;

  const changeImg = (dir) => {
    setImgIndex((imgIndex + dir + images.length) % images.length);
  };

  const formatPrice = (price) => {
    if (!price) return 'Consultar';
    return '$ ' + Number(price).toLocaleString('es-CL');
  };

  const specs = [
    { icon: '📅', label: 'Año', value: vehicle.year },
    { icon: '🔢', label: 'Kilómetros', value: vehicle.kilometers ? Number(vehicle.kilometers).toLocaleString('es-CL') + ' km' : null },
    { icon: '⚙️', label: 'Transmisión', value: vehicle.transmission },
    { icon: '⛽', label: 'Combustible', value: vehicle.fuelType },
    { icon: '🎨', label: 'Color', value: vehicle.color },
    { icon: '📍', label: 'Ciudad', value: vehicle.city },
    { icon: '🗺️', label: 'Región', value: vehicle.region },
  ].filter(s => s.value);

  const metaTitle = vehicle
    ? `${vehicle.title}${vehicle.price ? ` — $${Number(vehicle.price).toLocaleString('es-CL')}` : ''} | Solo Autos`
    : 'Vehículo | Solo Autos';
  const metaDesc = vehicle
    ? [vehicle.title, vehicle.year, vehicle.kilometers ? `${Number(vehicle.kilometers).toLocaleString('es-CL')} km` : null, vehicle.city, vehicle.region, vehicle.price ? `$${Number(vehicle.price).toLocaleString('es-CL')}` : null].filter(Boolean).join(' · ')
    : '';
  const metaImage = vehicle?.images?.[0]?.url || '';
  const metaUrl = `https://solo-autos.cl/vehiculo/${slug}`;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 20px 60px' }}>

      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:type" content="website" />
        {metaImage && <meta property="og:image" content={metaImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        {metaImage && <meta name="twitter:image" content={metaImage} />}
      </Helmet>

      {/* Volver */}
      <button
        onClick={() => window.history.back()}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#C31617', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, marginBottom: 16 }}
      >
        <RiArrowLeftLine /> Volver a resultados
      </button>

      {/* Título */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: '#111', margin: 0 }}>{vehicle.title}</h2>
          <ProviderBadge name={isOwn ? 'SoloAutos' : vehicle.providerName} bgColor={vehicle.providerBgColor} textColor={vehicle.providerTextColor} />
        </div>
        <div style={{ display: 'flex', gap: 20, marginTop: 6, fontSize: 13, color: '#7a7a7a' }}>
          {vehicle.city && <span>📍 {vehicle.city}{vehicle.region ? ', ' + vehicle.region : ''}</span>}
        </div>
      </div>

      {/* Body grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 20 }}
           className="vd-grid">

        {/* IZQUIERDA */}
        <div>
          {images.length > 0 && (
            <>
              <div style={{ borderRadius: 20, overflow: 'hidden', background: '#111', position: 'relative', boxShadow: '0 4px 16px rgba(0,0,0,.15)' }}>
                <img
                  src={img?.url}
                  alt={img?.alt || 'Foto del vehículo'}
                  style={{ width: '100%', maxHeight: 420, objectFit: 'cover', display: 'block' }}
                  onError={(e) => { e.target.src = 'https://placehold.co/800x450/1a1a1a/fff?text=Foto+no+disponible'; }}
                />
                {images.length > 1 && (
                  <>
                    <button onClick={() => changeImg(-1)} style={arrowStyle('left')}>‹</button>
                    <button onClick={() => changeImg(1)} style={arrowStyle('right')}>›</button>
                    <span style={{ position: 'absolute', bottom: 12, right: 14, background: 'rgba(0,0,0,.5)', color: 'white', fontSize: 12, padding: '3px 10px', borderRadius: 20 }}>
                      {imgIndex + 1} / {images.length}
                    </span>
                  </>
                )}
              </div>

              {images.length > 1 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {images.map((im, i) => (
                    <img
                      key={i}
                      src={im.url}
                      alt={im.alt || `Foto ${i + 1}`}
                      onClick={() => setImgIndex(i)}
                      style={{
                        width: 80, height: 54, borderRadius: 10, objectFit: 'cover', cursor: 'pointer',
                        border: i === imgIndex ? '2px solid #C31617' : '2px solid transparent',
                        opacity: i === imgIndex ? 1 : 0.55, transition: '.2s'
                      }}
                      onError={(e) => { e.target.src = 'https://placehold.co/160x100/1a1a1a/fff?text=' + (i+1); }}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {vehicle.summary && (
            <div style={{ marginTop: 16, background: 'white', borderRadius: 20, padding: '18px 20px', boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
              <h6 style={{ fontWeight: 700, marginBottom: 8, color: '#111' }}>Descripción</h6>
              <p style={{ fontSize: 13, lineHeight: 1.7, color: '#7a7a7a', margin: 0 }}>{vehicle.summary}</p>
            </div>
          )}
        </div>

        {/* SIDEBAR */}
        <div style={{ background: 'white', borderRadius: 20, padding: 22, boxShadow: '0 4px 16px rgba(0,0,0,.08)', display: 'flex', flexDirection: 'column', gap: 18, position: 'sticky', top: 82, height: 'fit-content' }}>

          <div>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#C31617', letterSpacing: '-0.5px' }}>
              {formatPrice(vehicle.price)}
            </div>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: 0 }} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {specs.map((s, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2, background: '#f5f5f5', borderRadius: 12, padding: '10px 12px' }}>
                <span style={{ fontSize: 15, color: '#C31617', marginBottom: 2 }}>{s.icon}</span>
                <span style={{ fontSize: 10, color: '#7a7a7a', textTransform: 'uppercase', letterSpacing: '.5px' }}>{s.label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{s.value}</span>
              </div>
            ))}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: 0 }} />

          {/* Contacto */}
          {isOwn && contact?.phone ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {contact.name && (
                <div style={{ fontSize: 13, color: '#7a7a7a', textAlign: 'center' }}>
                  Vendedor: <strong style={{ color: '#111' }}>{contact.name}</strong>
                </div>
              )}
              <a
                href={whatsappUrl(contact.phone, `Hola, me interesa tu auto: ${vehicle.title}`)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#25D366', color: 'white', fontWeight: 700, fontSize: 15, padding: '12px 20px', borderRadius: 12, textDecoration: 'none', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <RiWhatsappFill size={20} /> WhatsApp
              </a>
              <a
                href={`tel:${cleanPhone(contact.phone)}`}
                style={{ background: '#f0f0f0', color: '#111', fontWeight: 600, fontSize: 15, padding: '12px 20px', borderRadius: 12, textDecoration: 'none', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <RiPhoneFill size={18} /> Llamar
              </a>
              {contact.email && (
                <a
                  href={`mailto:${contact.email}?subject=${encodeURIComponent(`Consulta por tu auto: ${vehicle.title}`)}&body=${encodeURIComponent(`Hola ${contact.name || ''},\n\nMe interesa tu publicación "${vehicle.title}" (${formatPrice(vehicle.price)}).\n\n¿Está disponible?\n\nSaludos`)}`}
                  style={{ background: '#f0f0f0', color: '#111', fontWeight: 600, fontSize: 15, padding: '12px 20px', borderRadius: 12, textDecoration: 'none', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  <RiMailFill size={18} /> Enviar email
                </a>
              )}
            </div>
          ) : isOwn ? (
            <div style={{ background: '#f5f5f5', borderRadius: 12, padding: '14px 20px', textAlign: 'center', fontSize: 13, color: '#7a7a7a' }}>
              Publicación en SoloAutos
            </div>
          ) : vehicle.url ? (
            <a
              href={vehicle.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#C31617', color: 'white', fontWeight: 700, fontSize: 15, padding: '12px 20px', borderRadius: 12, textDecoration: 'none', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              Ver anuncio original <RiExternalLinkLine />
            </a>
          ) : null}

          {/* Compartir */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, color: '#7a7a7a' }}>
            <span>Compartir</span>
            <div style={{ display: 'flex', gap: 14, fontSize: 22 }}>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                 target="_blank" rel="noopener noreferrer" style={{ color: '#bbb', textDecoration: 'none' }}
                 onMouseOver={e => e.currentTarget.style.color='#C31617'} onMouseOut={e => e.currentTarget.style.color='#bbb'}>
                <RiFacebookCircleFill />
              </a>
              <a href={`https://wa.me/?text=${encodeURIComponent(`${vehicle.title} - ${formatPrice(vehicle.price)}\n${shareUrl}`)}`}
                 target="_blank" rel="noopener noreferrer" style={{ color: '#bbb', textDecoration: 'none' }}
                 onMouseOver={e => e.currentTarget.style.color='#C31617'} onMouseOut={e => e.currentTarget.style.color='#bbb'}>
                <RiWhatsappFill />
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .vd-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

const arrowStyle = (side) => ({
  position: 'absolute', top: '50%', transform: 'translateY(-50%)',
  [side]: 12,
  background: 'rgba(0,0,0,.45)', color: 'white', border: 'none', borderRadius: '50%',
  width: 36, height: 36, fontSize: 22, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});
